import type { UpdateInfo } from '../../shared/types'

export const HEADLESS_UPDATE_INSTALLING_EVENT = 'okclawpro:headless-update-installing'
export const HEADLESS_UPDATE_CLEARED_EVENT = 'okclawpro:headless-update-cleared'

const STORAGE_KEY = 'okclawpro.headlessUpdate.installing'
const MAX_INSTALLING_AGE_MS = 30 * 60_000

export interface HeadlessUpdateInstallingState {
  version: string | null
  startedAt: number
}

function getStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null
  } catch {
    return null
  }
}

function readInstallingState(): HeadlessUpdateInstallingState | null {
  const storage = getStorage()
  if (!storage) return null

  try {
    const raw = storage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<HeadlessUpdateInstallingState>
    if (typeof parsed.startedAt !== 'number') return null
    if (Date.now() - parsed.startedAt > MAX_INSTALLING_AGE_MS) {
      storage.removeItem(STORAGE_KEY)
      return null
    }
    return {
      version: typeof parsed.version === 'string' ? parsed.version : null,
      startedAt: parsed.startedAt,
    }
  } catch {
    storage.removeItem(STORAGE_KEY)
    return null
  }
}

export function isHeadlessUpdateInProgress(): boolean {
  return readInstallingState() !== null
}

export function markHeadlessUpdateInstalling(info?: Pick<UpdateInfo, 'latestVersion'> | null): void {
  const state: HeadlessUpdateInstallingState = {
    version: info?.latestVersion ?? null,
    startedAt: Date.now(),
  }

  try {
    getStorage()?.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Best effort: the in-memory CustomEvent still updates mounted UI.
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(HEADLESS_UPDATE_INSTALLING_EVENT, { detail: state }))
  }
}

export function clearHeadlessUpdateInstalling(): void {
  try {
    getStorage()?.removeItem(STORAGE_KEY)
  } catch {
    // Ignore disabled storage.
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(HEADLESS_UPDATE_CLEARED_EVENT))
  }
}

export function subscribeHeadlessUpdateInstalling(callback: (installing: boolean) => void): () => void {
  if (typeof window === 'undefined') return () => {}

  const handleInstalling = () => callback(true)
  const handleCleared = () => callback(false)
  window.addEventListener(HEADLESS_UPDATE_INSTALLING_EVENT, handleInstalling)
  window.addEventListener(HEADLESS_UPDATE_CLEARED_EVENT, handleCleared)

  return () => {
    window.removeEventListener(HEADLESS_UPDATE_INSTALLING_EVENT, handleInstalling)
    window.removeEventListener(HEADLESS_UPDATE_CLEARED_EVENT, handleCleared)
  }
}
